/**
 * Standing at ease in a figure's own stance (ท่ายืน; rig/skeleton.ts `STANCES`), each as two key
 * poses, the breath out and the breath in, looped by its motion.
 */
import type { Rig, MotionDef, PoseRigs } from './kit';
import type { MotionId } from './index';

const standing = (): Partial<Rig> => {
  // Standing at ease, as 'idle'; the stance (rig/skeleton.ts `STANCES`) sets the feet and hands.
  return { f1: -2, f2: 1 };
};

/** This group's poses. */
export type Poses =
  | 'standRooted'
  | 'standRootedIn'
  | 'standSword'
  | 'standSwordIn'
  | 'standMaster'
  | 'standMasterIn'
  | 'standPoised'
  | 'standPoisedIn'
  | 'standHip'
  | 'standHipIn'
  | 'standTurned'
  | 'standTurnedIn';

export const POSES: PoseRigs<Poses> = {
  standRooted: standing,
  standRootedIn: standing,
  standSword: standing,
  standSwordIn: standing,
  standMaster: standing,
  standMasterIn: standing,
  standPoised: standing,
  standPoisedIn: standing,
  standHip: standing,
  standHipIn: standing,
  standTurned: standing,
  standTurnedIn: standing,
};

export const MOTIONS = [
  // Standing at ease in each stance (ท่ายืน, rig/skeleton.ts `STANCES`): breathing in and out again,
  // the weight settling onto the standing leg. Drawn as the figure's 'idle' frames.
  { id: 'stanceRooted', keys: ['standRooted', 'standRootedIn'], ease: ['inOut', 'inOut'], loop: true },
  { id: 'stanceSword', keys: ['standSword', 'standSwordIn'], ease: ['inOut', 'inOut'], loop: true },
  { id: 'stanceMaster', keys: ['standMaster', 'standMasterIn'], ease: ['inOut', 'inOut'], loop: true },
  { id: 'stancePoised', keys: ['standPoised', 'standPoisedIn'], ease: ['inOut', 'inOut'], loop: true },
  { id: 'stanceHip', keys: ['standHip', 'standHipIn'], ease: ['inOut', 'inOut'], loop: true },
  { id: 'stanceTurned', keys: ['standTurned', 'standTurnedIn'], ease: ['inOut', 'inOut'], loop: true },
] as const satisfies readonly MotionDef[];

/**
 * Stances (ท่ายืน, rig/skeleton.ts `STANCES`) are picked per person, not per look: a standing frame's
 * phase carries which of the figure's three stances it is drawn in, as `k × STANCE_PH` over the
 * frame's own phase (every phase in use is far below it). Only standing frames carry it, so a look
 * draws (and bakes) three of each standing frame and shares every other one.
 */
export const STANCE_PH = 4096;
/** Stances each figure has to stand in (three per sex). */
export const STANCE_COUNT = 3;

/** Which of its stances an entity stands in: a hash of its id, the same every frame and every visit. */
export function stanceOf(id: number): number {
  let h = Math.imul(id ^ 0x9e3779b9, 0x85ebca6b);
  h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35);
  return ((h ^ (h >>> 16)) >>> 0) % STANCE_COUNT;
}

/** The stance loops: only ever drawn as a figure's standing frames ('idle'), never as 'motion'. */
export const STANCE_MOTION_IDS: ReadonlySet<MotionId> = new Set(MOTIONS.map((m) => m.id));
